/**
 * The gallery paints its dark editorial background by forcing white/grey text
 * onto every text-bearing element beneath it, so arbitrary CMS module markup
 * reads on black. Product-tagging chrome — the in-image tag card and all four
 * tagging modal screens — is a light glass panel living inside that same
 * subtree, so every rule that can reach it stops at `.product-tagging-chrome`
 * instead of repainting it afterwards. Repainting flattened the panel's own
 * hierarchy: result titles and their supplier lines are separate greys
 * (charcoal 300 / charcoal 100), as are the `text-gray-500` helper captions and
 * the tag card's supplier link. Excluding the chrome lets each of those
 * authored colours — and weights — stand as its component wrote them.
 *
 * The chrome root then takes the body colour so anything inside it that carries
 * no colour of its own inherits dark-on-light rather than the white it would
 * otherwise pick up from the gallery.
 *
 * Shared by the public gallery section and the Business Manager page builder so
 * the editing surface cannot drift from what the visitor sees. Always scope it
 * to a container — the legacy and light pages must stay untouched.
 */
export const PROJECT_MODULE_DARK_TEXT = [
    '[&_.editor-quote]:border-white/20!',
    '[&_.ui-caption:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:font-sans!',
    '[&_.ui-caption:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:font-normal!',
    '[&_.ui-caption:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-[#cbcbcb]!',
    // The muted link colour is for links inside the copy. A platform button
    // that happens to be an anchor — the tagged product's View product — takes
    // its own ink from the button, so it is exempt the way the tagging chrome is.
    '[&_a:not(.product-tagging-chrome):not(.product-tagging-chrome_*):not(.detail-cta-pill)]:font-sans!',
    '[&_a:not(.product-tagging-chrome):not(.product-tagging-chrome_*):not(.detail-cta-pill)]:text-[#a3a3a3]!',
    '[&_blockquote:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_div:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h1:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h2:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h3:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h4:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h5:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_h6:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_li:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_p:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:font-sans!',
    '[&_p:not(.product-tagging-chrome):not(.product-tagging-chrome_*)]:text-white!',
    '[&_.product-tagging-chrome]:text-[#1f1f21]!',
].join(' ');

/**
 * Figma spaces the gallery modules 3.12rem (50px) apart on desktop, overriding
 * the renderer's default gap-12. The module list is a single-column grid with an
 * implicit `auto` track, so one module wider than the column (a full-bleed block
 * in some galleries) sizes the track to its max-content and drags every module —
 * including the centred full-page images — right. Pin the track to the container
 * so a wide module can overflow on its own without shifting the rest.
 */
export const PROJECT_MODULE_GRID = 'grid-cols-[minmax(0,1fr)] md:gap-[3.12rem]';
